import { useState, useCallback, useEffect, useRef } from 'react';

import analytics from '@repo/analytics';
import {
  userStoreSelector,
  useUserStore,
} from '@repo/editor/src/stores/userStore';
import { withEventScope } from '@repo/event-processor';
import { Spacer } from '@repo/ui/components/Spacer';
import { H5 } from '@repo/ui/components/Typography';

import CommentBox from 'components/CommentBox/CommentBox';
import { TEMPLATE_COMMENT } from 'global/analyticsEvents';
import Api from 'global/commentsApi';
import { Comment } from 'global/types/Comment';

import useUserReference from '../hooks/useUserReference';

import CommentItem from './CommentItem/CommentItem';
import {
  CommentsContainer,
  CommentsTotal,
  CommentsWrapper,
  EmptyState,
  EmptyStateWrapper,
  ErrorMessage,
  TryAgain,
  SeeAllButton,
} from './styles';
import { defaultTheme } from './theme';
import { CommentListProps, CommentsState } from './types';

const CommentList = ({
  post,
  onUpdate,
  isNewsfeed,
  closeParent,
  theme,
  modalRef,
}: CommentListProps) => {
  const [comments, setComments] = useState<CommentsState>({
    status: 'pending',
    data: [],
    error: null,
    total: 0,
  });
  const user = useUserStore(userStoreSelector.user);
  const getUserBySlug = useUserReference();
  const abortRef = useRef<AbortController>();

  const fetchComments = useCallback(
    async (take = !isNewsfeed ? 15 : 2) => {
      setComments((state) => ({ ...state, status: 'pending' }));
      abortRef.current = new AbortController();

      try {
        const response = await Api.getComments({
          postId: post.id,
          take: !isNewsfeed && post.comments ? post.comments : take,
          signal: abortRef.current.signal,
        });
        if (response.error !== null) {
          return setComments((comments) => ({
            ...comments,
            status: 'rejected',
            error: response.error,
          }));
        }

        // Show the newest comments first
        const commentsList = response.comments;

        setComments({
          status: 'resolved',
          error: null,
          data: commentsList || [],
          total: response.total,
        });
      } catch (error) {
        if (typeof error === 'object' && error !== null && 'message' in error) {
          const message = (error as Error).message;
          if (message.includes('user aborted a request')) {
            return setComments((comments) => ({
              ...comments,
              status: 'idle',
            }));
          }

          return setComments((comments) => ({
            ...comments,
            status: 'rejected',
            error: message,
          }));
        }
      }
    },
    [isNewsfeed, post.id, post.comments]
  );

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  useEffect(() => {
    if (onUpdate) {
      onUpdate(comments);
    }
  }, [comments, onUpdate]);

  const commentsWrapperTheme = { ...defaultTheme, ...theme };

  const deleteComment = (comment: Comment) => {
    if (comment.replies) {
      setComments((state) => ({
        ...state,
        data: state.data.map((c) => {
          if (c.id === comment.id) {
            return { ...c, deleted: true };
          } else {
            return c;
          }
        }),
      }));
      return;
    }

    setComments((state) => ({
      ...state,
      total: state.total - 1,
      data: state.data.filter((c) => c.id !== comment.id),
    }));

    if (comments.status === 'pending') {
      abortRef.current?.abort();
      fetchComments(comments.total - 1);
    }
  };

  const handleNewComment = (newComment: Comment) => {
    // We want to show the newly added comment first
    setComments((state) => ({
      ...state,
      total: state.total + 1,
      data: [{ ...newComment, isNew: true }, ...state.data],
    }));

    if (comments.status === 'pending') {
      abortRef.current?.abort();
      fetchComments(comments.total + 1);
    }

    analytics.track(TEMPLATE_COMMENT, {
      templateId: post.template.id,
      templateName: post.template.name,
    });
  };

  const handleTryAgain = () => {
    fetchComments();
  };

  if (comments.status === 'rejected') {
    return (
      <ErrorMessage>
        {comments.error}{' '}
        <TryAgain onClick={handleTryAgain}>Try again?</TryAgain>
      </ErrorMessage>
    );
  }

  return (
    <CommentsWrapper theme={commentsWrapperTheme}>
      {user && (
        <>
          <CommentBox
            getUserBySlug={getUserBySlug}
            modalRef={modalRef}
            postId={post.id}
            onNewComment={handleNewComment}
          />
          <Spacer height="20px" />
        </>
      )}

      <CommentsTotal>
        {comments.status === 'resolved' && !comments.total && (
          <EmptyStateWrapper>
            <H5>No comments yet</H5>
            {user && <EmptyState>Be the first to comment</EmptyState>}
          </EmptyStateWrapper>
        )}
        {comments.total > 0 && (
          <H5>
            {comments.total} Comment{comments.total > 1 && 's'}
          </H5>
        )}
        {comments.total > comments.data.length && (
          <SeeAllButton
            disabled={comments.status === 'pending'}
            onClick={() => fetchComments(100)}
          >
            {comments.status === 'pending'
              ? 'Loading comments...'
              : 'See all comments'}
          </SeeAllButton>
        )}
      </CommentsTotal>

      <CommentsContainer data-testid="design-post--comments-list">
        {comments.data.map((comment) => (
          <CommentItem
            key={comment.id}
            closeParent={closeParent}
            comment={comment}
            getUserBySlug={getUserBySlug}
            modalRef={modalRef}
            postId={post?.id}
            onDelete={deleteComment}
          />
        ))}
      </CommentsContainer>
    </CommentsWrapper>
  );
};

export default withEventScope(CommentList, { module: 'commentList' });
